import { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Toggle from "../controls/Toggle";
import { updateNotificationPermissionsDeployment, updateNotificationPermissionsEnvironment, updateNotificationPermissionsDomain } from "./permissions/notifications";

const PERMISSION_TYPES_FOR_AUTO_CHECKING = ['notification', 'notificationTemplate', 'forms', 'formsInstance', 'formsCompleter'];

const AssignedPermissions = ({ domain, onChange, userPermissions, updatedAssignedPermissions, allPermissions }) => {
    const [assignedPermissions, setAssignedPermissions] = useState({});
    const [domainsPermissions, setDomainsPermissions] = useState([]);
    const [environmentsPermissions, setEnvironmentsPermissions] = useState([]);

    const [translate] = useTranslation();

    useEffect(() => {
        setDomainsPermissions(allPermissions.filter(p => p.domain._id === domain && p.environment === null && p.deployment === null));

        const environmentPermissions = allPermissions.filter(p => p.domain._id === domain && p.environment !== null && p.deployment === null);
        const uniqueEnvironments = new Set(environmentPermissions.map(p => p.environment._id));
        setEnvironmentsPermissions((permissions) => {
            permissions = [];
            uniqueEnvironments.forEach((id) => {
                permissions[id] = environmentPermissions.filter(p => p.environment._id === id);
            });

            return permissions;
        });
    }, [allPermissions, domain]);

    useEffect(() => {
        const updatedPermissions = {};

        allPermissions.forEach(permission => {
            if (updatedAssignedPermissions.includes(permission._id)) {
                permission.assigned = true;
            } else {
                permission.assigned = false;
            }
            updatedPermissions[permission._id] = permission;
        });

        setAssignedPermissions(updatedPermissions);
    }, [allPermissions, updatedAssignedPermissions]);

    const handleChange = useCallback((permissionId) => {
        const newObj = { ...assignedPermissions };

        const permission = newObj[permissionId];

        permission.assigned = !permission.assigned;

        if (PERMISSION_TYPES_FOR_AUTO_CHECKING.includes(permission.type)) {
            if (permission.deployment !== null) {
                updateNotificationPermissionsDeployment(newObj, permission);
            }
            else if (permission.environment !== null) {
                updateNotificationPermissionsEnvironment(newObj, permission);
            }
            else {
                updateNotificationPermissionsDomain(newObj, permission);
            }
        }

        setAssignedPermissions(newObj);
        onChange(newObj);
    }, [assignedPermissions, setAssignedPermissions, onChange]);

    const renderPermissionsList = useCallback(({ permissions, css }) => {
        if (permissions.length > 0) {
            return (
                <>
                    {permissions.map(permission => (
                        <div className={`${css}`} key={permission._id}>
                            <div className="form-check form-switch">
                                <Toggle
                                    id={permission._id}
                                    defaultState={assignedPermissions[permission._id]?.assigned}
                                    onChange={handleChange}
                                    disabled={!userPermissions.update}
                                />
                                <label className="form-check-label" htmlFor={permission._id}>{permission.name}</label>
                            </div>
                        </div>
                    ))}
                </>
            )
        }
    }, [userPermissions.update, handleChange, assignedPermissions]);

    const renderPermissionsCard = useCallback((title, name, permissions) => {
        return (
            <>
                <label className="d-block form-label h6">{`${title}${name ? `: ${name}` : ""}`}</label>
                {permissions &&
                    <div className="row mt-n2">
                        {renderPermissionsList({ permissions, css: 'col-12 col-lg-4 mt-2' })}
                    </div>}
            </>
        );
    }, [renderPermissionsList]);

    const renderDomainPermissions = useMemo(() => {
        if (domainsPermissions.length > 0) {
            return (
                <>
                    <label className="d-block form-label h6">{translate("OTM_DOMAIN_PERMISSIONS")}</label>
                    <div id="domainPermissions" className="row mt-n2">
                        {renderPermissionsList({ permissions: domainsPermissions, css: 'col-12 col-lg-4 mt-2' })}
                    </div>
                </>
            );
        }
    }, [domainsPermissions, renderPermissionsList, translate]);

    const renderDeploymentsCard = useCallback((environmentId) => {
        let allDeploymentsPermissions = allPermissions.filter(p => p.domain._id === domain && p.environment !== null && p.deployment !== null);
        allDeploymentsPermissions = allDeploymentsPermissions.filter(p => p.environment._id === environmentId);
        if (allDeploymentsPermissions.length === 0) {
            return (
                <div className="mt-3">
                    {renderPermissionsCard(translate("OTM_NO_DEPLOYMENTS"))}
                </div>
            )

        }

        const uniqueDeployments = new Set(allDeploymentsPermissions.map(p => p.deployment.name));
        const deploymentsPermissions = [...uniqueDeployments].map(name => ({ name, values: allDeploymentsPermissions.filter(p => p.deployment.name === name) }));

        return deploymentsPermissions.map((deployment, index) => (
            <div key={index} className="mt-3">
                {renderPermissionsCard(translate("OTM_DEPLOYMENT"), deployment.name, deployment.values)}
            </div>
        ))

    }, [translate, allPermissions, domain, renderPermissionsCard]);

    const renderEnvironmentsCard = useMemo(() => {
        if (Object.keys(environmentsPermissions).length === 0) {
            return <div className="mt-3">
                {renderPermissionsCard(translate("OTM_NO_ENVIRONMENTS"))}
            </div>
        }

        return (
            <ul className="list-group list-group-flush border-top mt-3">
                {Object.keys(environmentsPermissions).map((key) => (
                    <li key={key} className="list-group-item px-0 py-3">
                        <div className="row">
                            <div className="col-12">
                                {renderPermissionsCard(translate("OTM_ENVIRONMENT"), environmentsPermissions[key][0].environment.name, environmentsPermissions[key])}
                            </div>
                            <div className="col-11 offset-1">
                                {renderDeploymentsCard(key)}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        )
    }, [environmentsPermissions, renderPermissionsCard, translate, renderDeploymentsCard]);

    return (
        <>
            {userPermissions.read &&
                <>
                    {renderDomainPermissions}
                    {renderEnvironmentsCard}
                </>}
            {!userPermissions.read &&
                <label>{translate("OTM_NO_USER_PERMISSION")}</label>
            }
        </>
    );
};

export default AssignedPermissions;